/**
 * VoiceTabContent — Voice tab content for the UnifiedBottomPanel.
 *
 * CharacterDesigner only. Shows:
 * - Voice name + play/pause button if out_voice_profile exists
 * - "Regenerate Voice" button to re-generate voice without re-running images
 * - "No voice yet" placeholder with Generate button if no voice profile
 * - Static waveform visualization bars
 */

import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '../../../../lib/utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VoiceTabContentProps {
  voiceName?: string
  voiceSampleUrl?: string | null
  speechText?: string
  onRegenerateVoice?: () => void
  isRunning?: boolean
  hasCharacterSheet?: boolean
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Static waveform bar heights (10 bars) — matches CharacterDesignerNode Quick Info Bar */
const WAVEFORM_BAR_HEIGHTS = [3, 5, 7, 4, 6, 8, 5, 3, 6, 4]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const VoiceTabContent = memo(function VoiceTabContent({
  voiceName,
  voiceSampleUrl,
  speechText,
  onRegenerateVoice,
  isRunning,
  hasCharacterSheet,
}: VoiceTabContentProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const hasVoice = !!voiceName

  const handlePlayPause = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!audioRef.current || !voiceSampleUrl) return

      if (isPlaying) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
        setIsPlaying(false)
      } else {
        audioRef.current.play().catch(() => setIsPlaying(false))
        setIsPlaying(true)
      }
    },
    [isPlaying, voiceSampleUrl]
  )

  // Reset playing state on audio end
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleEnded = () => setIsPlaying(false)
    audio.addEventListener('ended', handleEnded)
    return () => audio.removeEventListener('ended', handleEnded)
  }, [])

  if (!hasVoice) {
    return (
      <div
        className="flex flex-col items-center justify-center h-full gap-3 text-text-muted"
        data-testid="voice-tab-placeholder"
      >
        <svg className="w-8 h-8 text-text-muted/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
        </svg>
        <span className="text-sm font-medium">Voice Profile</span>
        {onRegenerateVoice && hasCharacterSheet ? (
          <button
            onClick={(e) => { e.stopPropagation(); onRegenerateVoice() }}
            disabled={isRunning}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-md transition-colors nodrag',
              isRunning
                ? 'bg-surface-1 text-text-muted cursor-not-allowed'
                : 'bg-primary/10 text-primary hover:bg-primary/20'
            )}
            data-testid="voice-generate-button"
          >
            {isRunning ? 'Voice being generated...' : 'Generate Voice'}
          </button>
        ) : (
          <span className="text-xs text-text-muted/60">Coming soon</span>
        )}
      </div>
    )
  }

  return (
    <div
      className="flex flex-col items-center justify-center h-full gap-3 p-4"
      data-testid="voice-tab-content"
    >
      {/* Voice name */}
      <span className="text-sm font-medium text-foreground">{voiceName}</span>

      {/* Waveform + play button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handlePlayPause}
          disabled={!voiceSampleUrl}
          className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors',
            voiceSampleUrl
              ? 'bg-surface-2 hover:bg-surface-3 text-foreground'
              : 'bg-surface-1 text-text-muted cursor-not-allowed'
          )}
          data-testid="voice-play-button"
        >
          {isPlaying ? (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        {/* Static waveform visualization */}
        <div className="flex items-center gap-1 h-8" data-testid="voice-waveform">
          {WAVEFORM_BAR_HEIGHTS.map((h, i) => (
            <div
              key={i}
              className={cn(
                'w-1 rounded-full transition-all',
                isPlaying ? 'bg-primary' : 'bg-text-muted/50'
              )}
              style={{ height: `${h * 3}px` }}
            />
          ))}
        </div>
      </div>

      {/* Hidden audio element */}
      {voiceSampleUrl && (
        <audio ref={audioRef} src={voiceSampleUrl} preload="none" />
      )}

      {/* Speech text — shown below waveform */}
      {speechText && (
        <p
          className="text-xs italic text-text-muted/70 text-center max-w-[280px] leading-relaxed"
          data-testid="voice-speech-text"
        >
          &ldquo;{speechText}&rdquo;
        </p>
      )}

      {/* Regenerate Voice button */}
      {onRegenerateVoice && (
        <button
          onClick={(e) => { e.stopPropagation(); onRegenerateVoice() }}
          disabled={isRunning}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded-md transition-colors nodrag',
            isRunning
              ? 'bg-surface-1 text-text-muted cursor-not-allowed'
              : 'bg-surface-2 text-foreground hover:bg-surface-3'
          )}
          data-testid="voice-regenerate-button"
        >
          {isRunning ? 'Voice being generated...' : 'Regenerate Voice'}
        </button>
      )}
    </div>
  )
})

VoiceTabContent.displayName = 'VoiceTabContent'

export { VoiceTabContent }
